import React from "react";
import { getErrorCopy } from "./errorCodes";
import "./ModalError.css";

/**
 * TIER (modal special case): Banner inside a modal.
 *
 * Use when an action submitted from inside a modal failed and the user
 * needs to see why without the modal closing. Place at the top of the
 * modal body, above the form.
 *
 * Don't show toasts for in-modal failures — the toast can be obscured by
 * the modal backdrop, AND the user loses context of what they were doing.
 *
 * Props:
 *   code      — error code (404/500/...) for default copy
 *   title     — override
 *   message   — override (or pass server's specific error string here)
 *   onDismiss — if provided, renders an × button
 *
 * Renders nothing when neither message nor code is provided, so you can
 * leave it in JSX unconditionally and it disappears when the error is
 * cleared.
 */
const ModalError = ({ code, title, message, onDismiss }) => {
  if (!code && !message && !title) return null;
  const defaults = getErrorCopy(code);
  return (
    <div className="modal-error" role="alert">
      <div className="modal-error-icon" aria-hidden="true">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10" />
          <line x1="12" y1="8" x2="12" y2="12" />
          <line x1="12" y1="16" x2="12.01" y2="16" />
        </svg>
      </div>
      <div className="modal-error-body">
        {(title || defaults.title) && (
          <div className="modal-error-title">{title || defaults.title}</div>
        )}
        <div className="modal-error-message">{message || defaults.message}</div>
      </div>
      {onDismiss && (
        <button
          type="button"
          className="modal-error-dismiss"
          onClick={onDismiss}
          aria-label="Dismiss"
        >
          ×
        </button>
      )}
    </div>
  );
};

export default React.memo(ModalError);
